/**
 * Phase 25 Plan 25-04 — typed client for the Phase 22 task-comment surface.
 *
 * Endpoints (locked Phase 22 D-22; no new mutation routes this phase):
 *   GET    /api/tasks/:taskId/comments
 *   POST   /api/tasks/:taskId/comments
 *   PATCH  /api/tasks/:taskId/comments/:commentId
 *   PATCH  /api/tasks/:taskId/comments/:commentId/visibility      (MKS-185, flagged)
 *   DELETE /api/tasks/:taskId/comments/:commentId
 *   POST   /api/tasks/:taskId/comments/:commentId/attachments     (multipart)
 *   GET    /api/tasks/:taskId/comments/:commentId/attachments/:id (signed URL)
 *
 * The single-attachment upload helper is hand-rolled (rather than reusing
 * `apiUpload`) because the endpoint shape `/tasks/:taskId/comments/:commentId
 * /attachments` doesn't fit the generic single-segment helper, and we want
 * the multipart `Content-Type` to remain browser-derived (so the boundary is
 * set automatically — see Pitfall in `apiUpload` comment).
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import { API_BASE_URL, getBaseAuthHeaders, ApiClientError } from './client';

export interface CommentAttachmentDTO {
  id: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface CommentDTO {
  id: number;
  taskId: number;
  authorUserId: number | null;
  authorExternalUserId: number | null;
  authorFirstName: string | null;
  authorLastName: string | null;
  authorEmail: string | null;
  body: string;
  isInternalOnly: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  /**
   * Phase 114 (Plan 114-04 / PCEXEC-01) — 'paperclip' | null. null covers
   * every ordinary human comment AND every Monka (Robot-authored) comment —
   * only Factory™ is stamped (D-114-04). Matches the backend CommentDTO
   * (taskCommentService.ts) exactly — always emitted, never absent.
   */
  sourceExecutor: string | null;
  attachments: CommentAttachmentDTO[];
}

export interface CreateCommentInput {
  body: string;
  isInternalOnly?: boolean;
}

export interface UpdateCommentInput {
  body: string;
}

/**
 * MKS-188 — per-file outcome of a multipart create. Mirrors the backend
 * `InlineAttachmentResult` and the hook's own `AttachmentResult`; `code` carries
 * the locked Phase 22 D-15 vocabulary.
 */
export interface CommentAttachmentResultDTO {
  filename: string;
  status: 'ok' | 'error';
  code?: string;
  message?: string;
}

interface ListResponse {
  data: CommentDTO[];
}
interface SingleResponse {
  data: CommentDTO;
}
interface CreateCommentResponse {
  data: CommentDTO;
  /** MKS-188 — absent on a JSON create against an older backend. */
  attachmentResults?: CommentAttachmentResultDTO[];
}
interface AttachmentResponse {
  data: CommentAttachmentDTO;
}
interface SignedUrlResponse {
  data: { url: string; expiresAt: string };
}

export async function listComments(taskId: number): Promise<CommentDTO[]> {
  const res = await apiGet<ListResponse>(`/tasks/${taskId}/comments`);
  return res.data;
}

export async function createComment(
  taskId: number,
  input: CreateCommentInput,
): Promise<CommentDTO> {
  const res = await apiPost<SingleResponse>(`/tasks/${taskId}/comments`, input);
  return res.data;
}

/**
 * MKS-188 — post the comment AND its attachments in one multipart request, so
 * the server persists the files before it notifies anyone. Replaces the
 * create-then-upload-each-file sequence, which always emailed watchers about a
 * comment whose screenshot could not exist yet.
 *
 * Field order matters: the server reads `body` / `isInternalOnly` off the head
 * of the stream (it needs them to create the comment the files will hang off),
 * so they MUST be appended before any file.
 *
 * A per-file failure does not fail the request — the comment is posted and the
 * outcome of each file comes back in `attachmentResults`, exactly the shape the
 * sequential path produced client-side.
 *
 * Pitfall — never set `Content-Type` here; see `uploadCommentAttachment`.
 */
export async function createCommentWithAttachments(
  taskId: number,
  input: CreateCommentInput,
  files: File[],
): Promise<{ comment: CommentDTO; attachmentResults: CommentAttachmentResultDTO[] }> {
  const fd = new FormData();
  fd.append('body', input.body);
  if (input.isInternalOnly !== undefined) {
    fd.append('isInternalOnly', String(input.isInternalOnly));
  }
  for (const file of files) {
    fd.append('files', file, file.name);
  }
  const res = await fetch(`${API_BASE_URL}/tasks/${taskId}/comments`, {
    method: 'POST',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({
      statusCode: res.status,
      error: 'Unknown',
      message: res.statusText,
    }));
    throw new ApiClientError(err);
  }
  const json = (await res.json()) as CreateCommentResponse;
  return { comment: json.data, attachmentResults: json.attachmentResults ?? [] };
}

export async function updateComment(
  taskId: number,
  commentId: number,
  input: UpdateCommentInput,
): Promise<CommentDTO> {
  const res = await apiPatch<SingleResponse>(
    `/tasks/${taskId}/comments/${commentId}`,
    input,
  );
  return res.data;
}

/**
 * MKS-185 — change an existing comment's visibility. Backed by its own route
 * (not a field on the edit body) so a plain edit can never flip visibility as a
 * side effect.
 */
export async function updateCommentVisibility(
  taskId: number,
  commentId: number,
  isInternalOnly: boolean,
): Promise<CommentDTO> {
  const res = await apiPatch<SingleResponse>(
    `/tasks/${taskId}/comments/${commentId}/visibility`,
    { isInternalOnly },
  );
  return res.data;
}

export async function deleteComment(taskId: number, commentId: number): Promise<void> {
  await apiDelete<void>(`/tasks/${taskId}/comments/${commentId}`);
}

/**
 * Upload a single attachment (multipart). Throws `ApiClientError` carrying
 * the locked Phase 22 D-15 codes on failure:
 *   `ATTACHMENT_TOO_LARGE` | `ATTACHMENT_DISALLOWED_TYPE` | `ATTACHMENT_TOO_MANY`.
 *
 * Pitfall — never set `Content-Type` here. The browser MUST pick the
 * `multipart/form-data; boundary=…` value itself; otherwise the boundary
 * is missing and the server cannot parse the body.
 */
export async function uploadCommentAttachment(
  taskId: number,
  commentId: number,
  file: File,
): Promise<CommentAttachmentDTO> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const res = await fetch(
    `${API_BASE_URL}/tasks/${taskId}/comments/${commentId}/attachments`,
    {
      method: 'POST',
      credentials: 'include',
      headers: getBaseAuthHeaders(),
      body: fd,
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({
      statusCode: res.status,
      error: 'Unknown',
      message: res.statusText,
    }));
    throw new ApiClientError(err);
  }
  const json = (await res.json()) as AttachmentResponse;
  return json.data;
}

export async function mintAttachmentDownloadUrl(
  taskId: number,
  commentId: number,
  attachmentId: number,
  disposition?: 'attachment',
): Promise<{ url: string; expiresAt: string }> {
  const query = disposition ? `?disposition=${disposition}` : '';
  const res = await apiGet<SignedUrlResponse>(
    `/tasks/${taskId}/comments/${commentId}/attachments/${attachmentId}${query}`,
  );
  return res.data;
}
